import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  BarcodeCapture,
  barcodeCaptureLoader,
  BarcodeCaptureOverlay,
  BarcodeCaptureSettings,
  Symbology,
} from 'scandit-web-datacapture-barcode'
import {
  Camera,
  CameraSwitchControl,
  configure,
  DataCaptureContext,
  DataCaptureView,
  FrameSourceState,
  LaserlineViewfinder,
} from 'scandit-web-datacapture-core'
import PropTypes from 'prop-types'

export function createSDKFacade() {
  let context
  let view
  let laserLineViewFinder
  let settings
  let barcodeCapture
  let overlay
  let host
  let cameraSwitchControl
  let camera

  function createHostElementIfNeeded() {
    if (!host) {
      host = document.createElement('div')
      host.style.display = 'none'
      host.style.width = '100%'
      host.style.height = '100%'
      document.body.append(host)
    }
    return host
  }

  return {
    async initialize() {
      await configure({
        libraryLocation: '/engine',
        licenseKey:
          'ARllByFECaRqPjr3RSRhDedD1xTD7ZOgw3QvFUEvVHNASLE+6XYGSuxR8tSxN8eJV36buoBx9f1VEu30v2AaUvBkTS0qelLEOyJ1xg9HwHmbAvTGvHSH21sAZqkPc4GZ/k091c8cBfnWGicjWCqI0KcJJ2RHOXYvghsJ9N+h+RE9hsLWSq8O3HczCz2/8Xq133nJWegqeSL7YS0e27mcNgwfjxhPPVTIEKFS2HEs4v4YuBe2JbN+SolDNyjVolOAbhAW8+U41gWJM4CMB9HRbXafg+PpvK+Mym0ImG8SClVcRrnQ95U06SLPpW1P7O3TSbaN+lDUxn7Qn3I2gku6UGEWACAQReFbGR98ZB+Re2RsqbvrDRf2kXJQ5SrQFWmyhZagMTLRSQ2TvkMDhVORlzRiJU1ZJv2sNQkT+OLt0sQ0pbiysEBvt8zAGoTP5bDAAIi0BLscBxOF1qqKP0Vg5OsLQg6/CJQpKV1qSoG+a0JwcDc20++6dAkX1Z0TEJPVwocKOBnb5xOgXLH/0TNks9O4AyqcToVCBE/Tqs/lfO5n7nu6TOL0sohOmdavL5O088OO43faAwNOVp9//CuwOP2w2vOpUZDrDlM8/DOFxXOYUiUHI3bZutdAbnl9OtcYVHCWnN3nJw20xlIUdSaX/DZnimXZaL2tY1nrC3V8nvq7C/VtyM2cnBvWMh+m7Shm/1BlKbTA/oQtMOp9n4azHuN5GFs0/UwtVwf2teHBABBJ7PmQ7jZJVmkZcolT1dVRorWUyk4ydX6pYftG/9P9XKlxVn2P6Tc+ZvQGrRUtlSfehUhRtNM/V6Age9Gk6ev6AkzUMqm2oxECAA==',
        moduleLoaders: [barcodeCaptureLoader()],
      })
      context = await DataCaptureContext.create()
      settings = new BarcodeCaptureSettings()
      settings.enableSymbologies([
        Symbology.EAN13UPCA,
        Symbology.EAN8,
        Symbology.UPCE,
        Symbology.QR,
        Symbology.DataMatrix,
        Symbology.Code39,
        Symbology.Code128,
        Symbology.InterleavedTwoOfFive,
      ])

      view = await DataCaptureView.forContext(context)
      view.connectToElement(createHostElementIfNeeded())

      cameraSwitchControl = new CameraSwitchControl()
      view.addControl(cameraSwitchControl)

      barcodeCapture = await BarcodeCapture.forContext(context, settings)
      await barcodeCapture.setEnabled(false)

      overlay = await BarcodeCaptureOverlay.withBarcodeCaptureForView(
        barcodeCapture,
        view,
      )
      laserLineViewFinder = new LaserlineViewfinder()
      await overlay.setViewfinder(laserLineViewFinder)
      await view.addOverlay(overlay)

      // Camera.default is null when the device has no camera
      camera = Camera.default
      if (camera) {
        await camera.applySettings(BarcodeCapture.recommendedCameraSettings)
        await context.setFrameSource(camera)
      }
    },
    async cleanup() {
      // teardown can fail when the camera is missing or init is still running
      try {
        await context?.frameSource?.switchToDesiredState(FrameSourceState.Off)
        await context?.removeAllModes()
        await context?.dispose()
        if (overlay) {
          await overlay.setViewfinder(null)
          await view?.removeOverlay(overlay)
        }
        if (cameraSwitchControl) {
          view?.removeControl(cameraSwitchControl)
        }
        view?.detachFromElement()
      } catch (error) {
        console.warn('VIN scanner cleanup failed:', error)
      }
      cameraSwitchControl = undefined
      overlay = undefined
      laserLineViewFinder = undefined
      barcodeCapture = undefined
      context = undefined
      view = undefined
      settings = undefined
      camera = undefined
      host?.remove()
      host = undefined
    },
    connectToElement(element) {
      host = createHostElementIfNeeded()
      host.style.display = 'block'
      element.append(host)
    },
    detachFromElement() {
      if (host) {
        host.style.display = 'none'
        document.body.append(host)
      }
    },
    async enableCamera(enabled) {
      if (context?.frameSource) {
        await context.frameSource.switchToDesiredState(
          enabled ? FrameSourceState.On : FrameSourceState.Off,
        )
      }
    },
    async enableScanning(enabled) {
      await barcodeCapture?.setEnabled(enabled)
    },
    async enableSymbology(symbology, enabled) {
      settings.enableSymbology(symbology, enabled)
      await barcodeCapture?.applySettings(settings)
    },
    addBarcodeCaptureListener(listener) {
      barcodeCapture?.addListener(listener)
    },
    removeBarcodeCaptureListener(listener) {
      barcodeCapture?.removeListener(listener)
    },
    getEnabledSymbologies() {
      return settings.enabledSymbologies
    },
  }
}

export const SDKContext = createContext({
  loaded: false,
  loading: false,
  sdk: null,
})

export default function SDKProvider({ children }) {
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const sdk = useMemo(() => createSDKFacade(), [])

  const providerValue = useMemo(
    () => ({ loading, loaded, sdk }),
    [loading, loaded, sdk],
  )

  useEffect(() => {
    let active = true

    // The camera is turned on by ScannerComponent only when the scan page
    // opens, so pages like /vin/decoder never touch it.
    async function start() {
      setLoading(true)
      try {
        await sdk.initialize()
        if (!active) {
          // page was left while init was running; undo what init created
          await sdk.cleanup()
          return
        }
        setLoaded(true)
      } catch (error) {
        // camera missing, permission denied, or SDK failed to load
        console.warn('VIN scanner unavailable:', error)
      } finally {
        if (active) setLoading(false)
      }
    }
    void start()
    return () => {
      active = false
      sdk.cleanup().catch((error) => {
        console.warn('VIN scanner cleanup failed:', error)
      })
    }
  }, [sdk])

  return (
    <SDKContext.Provider value={providerValue}>{children}</SDKContext.Provider>
  )
}

SDKProvider.propTypes = {
  children: PropTypes.any,
}

export function useSDK() {
  const value = useContext(SDKContext)

  if (value.sdk === null) {
    throw new Error(
      'Sdk facade is null. Did you forget to wrap the component with SDKProvider?',
    )
  }
  return value
}
